import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import {
  getAreaLabel,
  getEnabledMetros,
  getMetroById,
  getMetroFilterAreas,
  HOMETOWN_STORAGE_KEY,
  METRO_OVERRIDE_STORAGE_KEY,
  GPS_OPT_IN_KEY,
  shouldShowAreaFilter,
} from '../data/metros';
import { filterBarsByScope, findMetroForCoordinates } from '../utils/geo';

const LocationContext = createContext(null);

function readStored(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key, value) {
  try {
    if (value == null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch { /* ignore */ }
}

export function LocationProvider({ children }) {
  const { user, profile, updateProfile } = useAuth();
  const enabledMetros = useMemo(() => getEnabledMetros(), []);
  const syncedHometownRef = React.useRef(false);

  const [hometownMetroId, setHometownMetroId] = useState(() => {
    return readStored(HOMETOWN_STORAGE_KEY) || profile?.hometown_metro_id || 'chicago';
  });
  const [metroOverrideId, setMetroOverrideId] = useState(() => readStored(METRO_OVERRIDE_STORAGE_KEY));
  const [gpsOptIn, setGpsOptIn] = useState(() => readStored(GPS_OPT_IN_KEY) === 'true');
  const [userLocation, setUserLocation] = useState(null);
  const [locationAccuracy, setLocationAccuracy] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);
  const [locationAsked, setLocationAsked] = useState(() => readStored('tilted_location_asked'));

  useEffect(() => {
    syncedHometownRef.current = false;
  }, [user?.id]);

  useEffect(() => {
    if (profile?.hometown_metro_id) {
      setHometownMetroId(profile.hometown_metro_id);
      writeStored(HOMETOWN_STORAGE_KEY, profile.hometown_metro_id);
      return;
    }
    const stored = readStored(HOMETOWN_STORAGE_KEY);
    if (stored && user && profile && !profile.hometown_metro_id && updateProfile && !syncedHometownRef.current) {
      syncedHometownRef.current = true;
      updateProfile({ hometown_metro_id: stored }).catch(() => {});
    }
  }, [profile?.hometown_metro_id, user, profile, updateProfile]);

  const hometownCandidate = getMetroById(hometownMetroId);
  const hometownMetro = hometownCandidate?.enabled ? hometownCandidate : getMetroById('chicago');
  const overrideCandidate = getMetroById(metroOverrideId);
  const overrideMetro = overrideCandidate?.enabled ? overrideCandidate : null;

  useEffect(() => {
    if (metroOverrideId && !getMetroById(metroOverrideId)?.enabled) {
      setMetroOverrideId(null);
      writeStored(METRO_OVERRIDE_STORAGE_KEY, null);
    }
  }, [metroOverrideId]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }

    setLocationLoading(true);
    setLocationError(null);

    const safetyTimer = setTimeout(() => {
      setLocationLoading(false);
      setLocationError('Location request timed out');
    }, 15000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        clearTimeout(safetyTimer);
        const { latitude, longitude, accuracy } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationAccuracy(accuracy);
        setLocationLoading(false);
        setLocationAsked('granted');
        writeStored('tilted_location_asked', 'granted');
      },
      (error) => {
        clearTimeout(safetyTimer);
        setLocationLoading(false);
        if (error.code === 1) {
          setLocationAsked('denied');
          writeStored('tilted_location_asked', 'denied');
          setLocationError('Permission denied');
        } else {
          setLocationError(error.message);
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, []);

  const enableGpsScope = useCallback(() => {
    setGpsOptIn(true);
    writeStored(GPS_OPT_IN_KEY, 'true');
    setMetroOverrideId(null);
    writeStored(METRO_OVERRIDE_STORAGE_KEY, null);
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation not supported');
      return;
    }
    if (gpsOptIn && readStored('tilted_location_asked') !== 'denied') {
      requestLocation();
    }
  }, [gpsOptIn, requestLocation]);

  const setHometown = useCallback(async (metroId) => {
    const metro = getMetroById(metroId);
    if (!metro?.enabled) return;
    setHometownMetroId(metro.id);
    writeStored(HOMETOWN_STORAGE_KEY, metro.id);
    setMetroOverrideId(metro.id);
    writeStored(METRO_OVERRIDE_STORAGE_KEY, metro.id);
    setGpsOptIn(false);
    writeStored(GPS_OPT_IN_KEY, null);
    if (user && updateProfile) {
      try {
        await updateProfile({ hometown_metro_id: metro.id });
      } catch (err) {
        console.log('Hometown profile update skipped:', err.message);
      }
    }
  }, [user, updateProfile]);

  const setMetroOverride = useCallback((metroId) => {
    if (!metroId) {
      setMetroOverrideId(null);
      writeStored(METRO_OVERRIDE_STORAGE_KEY, null);
      return;
    }
    const metro = getMetroById(metroId);
    if (!metro?.enabled) return;
    setMetroOverrideId(metro.id);
    writeStored(METRO_OVERRIDE_STORAGE_KEY, metro.id);
  }, []);

  const gpsMetro = userLocation
    ? findMetroForCoordinates(userLocation.lat, userLocation.lng, enabledMetros)
    : null;

  const usingOverride = Boolean(overrideMetro);
  const usingGps = gpsOptIn && !usingOverride && Boolean(userLocation);
  // "Near me" stays selected while the GPS fix is loading; falls back to the city once location fails
  const nearMeSelected = gpsOptIn && !usingOverride && !locationError;
  const activeMetro = overrideMetro || (usingGps ? gpsMetro : null) || hometownMetro || getMetroById('chicago');

  const scopeOrigin = usingOverride
    ? overrideMetro.center
    : usingGps
      ? userLocation
      : (hometownMetro || activeMetro).center;

  const radiusMiles = usingGps ? 50 : (activeMetro?.radiusMiles || 50);

  const mapCenter = [scopeOrigin.lat, scopeOrigin.lng];

  const getScopedBars = useCallback((bars) => {
    return filterBarsByScope(bars, {
      origin: scopeOrigin,
      radiusMiles,
      metroId: usingGps ? null : activeMetro?.id,
      metros: enabledMetros,
    });
  }, [scopeOrigin, radiusMiles, usingGps, activeMetro?.id, enabledMetros]);

  const persistHometownIfMissing = useCallback(async () => {
    if (!user || profile?.hometown_metro_id) return;
    try {
      await supabase
        .from('profiles')
        .update({ hometown_metro_id: hometownMetroId })
        .eq('id', user.id);
    } catch { /* column may not exist yet */ }
  }, [user, profile?.hometown_metro_id, hometownMetroId]);

  const value = {
    enabledMetros,
    hometownMetroId,
    hometownMetro,
    setHometown,
    metroOverrideId,
    setMetroOverride,
    activeMetro,
    userLocation,
    userLocationArray: userLocation ? [userLocation.lat, userLocation.lng] : null,
    locationAccuracy,
    locationLoading,
    locationError,
    locationAsked,
    requestLocation,
    enableGpsScope,
    gpsOptIn,
    usingGps,
    nearMeSelected,
    usingOverride,
    scopeOrigin,
    radiusMiles,
    mapCenter,
    getScopedBars,
    showAreaFilter: shouldShowAreaFilter(activeMetro),
    areaLabel: getAreaLabel(activeMetro),
    areas: getMetroFilterAreas(activeMetro),
    needsHometown: Boolean(user && profile)
      && !profile.hometown_metro_id
      && !readStored(HOMETOWN_STORAGE_KEY),
    persistHometownIfMissing,
  };

  return (
    <LocationContext.Provider value={value}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocationScope() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocationScope must be used within a LocationProvider');
  }
  return context;
}
